import { useCallback, useEffect, useMemo } from 'react';

import { observer } from 'mobx-react-lite';
import { useHistory, useParams } from 'react-router-dom';

import Page from 'bringg-web/features/order-history-page/components/page/page';
import { useStores } from 'bringg-web/recipes';
import CssBaseline from '../components/css-baseline/css-baseline';
import TrafficCalendar from './components/traffic-calendar';
import { useSpeedFactorTranslation } from './translations';
import { useGetServiceAreaRootUrl } from '../utils/use-get-service-area-root-url';
import { type TimezoneOption } from './types';

const TrafficMatrixPage = observer(() => {
	const { serviceAreasStore, teamsStore } = useStores();
	const { id: serviceAreaId } = useParams<{ id: string }>();
	const history = useHistory();
	const translations = useSpeedFactorTranslation();

	const featureRootUrl = useGetServiceAreaRootUrl();

	const serviceArea = useMemo(
		() => serviceAreasStore.serviceAreas.get(Number(serviceAreaId)),
		[serviceAreasStore.serviceAreas, serviceAreaId]
	);

	useEffect(() => {
		if (!serviceAreasStore.isFetched) {
			return;
		}

		if (!serviceArea) history.replace(featureRootUrl);
	}, [serviceAreasStore.isFetched, serviceArea, featureRootUrl]);

	useEffect(() => {
		const fetchData = async () => await serviceAreasStore.fetchAll();

		fetchData();
	}, [serviceAreaId]);

	const handleBack = useCallback(
		() => history.push(`${featureRootUrl}?id=${serviceAreaId}`),
		[serviceAreaId, featureRootUrl]
	);

	const teamTimezones = useMemo<TimezoneOption[]>((): TimezoneOption[] => {
		if (!serviceArea) return [];
		return (serviceArea.team_ids || []).reduce((acc, teamId) => {
			const team = teamsStore.get(teamId);
			if (team?.localizationTimezone) {
				acc.push({ label: team.name, timezone: team.localizationTimezone });
			}
			return acc;
		}, []);
	}, [serviceArea]);

	if (!serviceArea) return null;

	return (
		<CssBaseline>
			<Page
				title={`${translations.pageTitle} – «${serviceArea.name}»`}
				subtitle={translations.pageSubtitle}
				rightGutter
				onBack={handleBack}
			>
				<TrafficCalendar serviceAreaId={Number(serviceAreaId)} additionalTimezones={teamTimezones} />
			</Page>
		</CssBaseline>
	);
});

export default TrafficMatrixPage;
